import { useMemo, useState } from 'react';
import { Button } from '@dataroom/shared';
import type { ImportDdRequestsRequest, ImportDdRequestsResponse } from '@dataroom/shared';
import { Modal } from './Modal';

interface BulkImportDdDialogProps {
  pending: boolean;
  error: string | null;
  /** The returned counts once an import has run (kept visible so the admin sees
   * what happened before closing). */
  result: ImportDdRequestsResponse | null;
  onClose: () => void;
  onSubmit: (body: ImportDdRequestsRequest) => void;
}

/** Parse the pasted textarea into `rows` — one non-empty line per requested item,
 * each `Phase name / Item title` (the server splits on the first '/'). Blank
 * lines are dropped. */
function toRows(text: string): string[] {
  return text
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.length > 0);
}

const PLACEHOLDER = `Phase 1 — Corporate & Financials / Audited financial statements (last 3 years)
Phase 1 — Corporate & Financials / Cap table
Phase 2 — Legal & Material Contracts / Top-20 customer contracts
Phase 2 — Legal & Material Contracts / Pending litigation summary`;

/**
 * Slice 6 "Bulk import" — load hundreds of requested items across phases in one
 * call. Takes pasted text, one `Phase / Item` per line, converted to the import
 * endpoint's flat `rows` form (phases and items are found-or-created, so a
 * re-import is idempotent). On success it shows the returned created/reused
 * counts. Presentational: the card owns the mutation.
 */
export function BulkImportDdDialog({
  pending,
  error,
  result,
  onClose,
  onSubmit,
}: BulkImportDdDialogProps) {
  const [text, setText] = useState('');
  const rows = useMemo(() => toRows(text), [text]);
  const canSubmit = rows.length > 0 && !pending;

  return (
    <Modal
      title="Import checklist items"
      description="Paste your checklist, one item per line as “Phase / Item”. Existing phases and items are matched by name, so re-importing is safe."
      onClose={onClose}
      footer={
        <>
          {error ? (
            <p className="text-sm text-destructive">{error}</p>
          ) : (
            <span className="text-xs text-muted-foreground">
              {rows.length} row{rows.length === 1 ? '' : 's'} ready to import.
            </span>
          )}
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={onClose} disabled={pending}>
              {result ? 'Done' : 'Cancel'}
            </Button>
            <Button size="sm" disabled={!canSubmit} onClick={() => onSubmit({ rows })}>
              {pending ? 'Importing…' : 'Import'}
            </Button>
          </div>
        </>
      }
    >
      <div className="space-y-3">
        <div className="space-y-1.5">
          <label htmlFor="dd-import" className="text-sm font-medium">
            Items
          </label>
          <textarea
            id="dd-import"
            rows={8}
            className="flex w-full rounded-md border border-input bg-background px-3 py-2 font-mono text-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
            placeholder={PLACEHOLDER}
            value={text}
            onChange={(e) => setText(e.target.value)}
            disabled={pending}
          />
          <p className="text-xs text-muted-foreground">
            One item per line. The text before the first “/” is the phase; the rest is the item.
          </p>
        </div>

        {result ? (
          <div className="rounded-md border border-border bg-muted/40 p-3 text-sm">
            <p className="font-medium">Import complete</p>
            <ul className="mt-1 space-y-0.5 text-xs text-muted-foreground">
              <li>
                Phases: {result.phasesCreated} created, {result.phasesReused} reused
              </li>
              <li>
                Items: {result.requestsCreated} created, {result.requestsReused} reused
              </li>
            </ul>
          </div>
        ) : null}
      </div>
    </Modal>
  );
}
